/**
 * governance.gql-map.ts — the PURE mapping layer between the B0.7' governance
 * graph shapes and the legacy REST types (B1.3').
 *
 * Deliberately import-free of the axios/i18n stack (axios.config pulls the
 * i18n boot, which touches `window` at module scope) so the vitest matrix can
 * exercise every derivation in the plain node environment. governance.api.ts
 * re-exports this surface — facade callers see one module.
 *
 * Mapping stance (contract-leads-SDL): fields the contract does not carry map
 * to '' (rendered '-' by the list-kit) rather than invented values; enum
 * normalization is case-tolerant with honest allowlist fallbacks.
 */

import type {
  GqlGovernanceAuditNode,
  GqlGovernanceRoleNode,
  GqlGovernanceUserNode,
  GqlOrganisationUnitNode,
} from './graphql.operations';
import type {
  AuditOperation,
  GovernanceAuditLog,
  GovernanceRecordStatus,
  GovernanceRole,
  GovernanceUser,
  GovernanceUserStatus,
  OrganisationUnit,
} from '@/types/api/governance';

/** Bilingual pair → the seed-data presentation style ("HOLIEN HQ 何聯"). */
export function pickBilingualName(nameEn: string | null, nameZh: string | null): string {
  if (nameEn && nameZh && nameEn !== nameZh) return `${nameEn} ${nameZh}`;
  return nameEn || nameZh || '';
}

function toRecordStatus(status: string): GovernanceRecordStatus {
  return status.toLowerCase() === 'inactive' ? 'inactive' : 'active';
}

const USER_STATUSES: readonly GovernanceUserStatus[] = ['active', 'disabled', 'invited'];

function toUserStatus(status: string): GovernanceUserStatus {
  const lowered = status.toLowerCase() as GovernanceUserStatus;
  return USER_STATUSES.includes(lowered) ? lowered : 'active';
}

const AUDIT_OPERATIONS: readonly AuditOperation[] = [
  'CREATE',
  'UPDATE',
  'DELETE',
  'APPROVE',
  'OVERRIDE',
];

function toAuditOperation(operation: string): AuditOperation {
  const uppered = operation.toUpperCase() as AuditOperation;
  return AUDIT_OPERATIONS.includes(uppered) ? uppered : 'UPDATE';
}

/**
 * Flatten the organisationUnits tree (3 levels selected by the operation)
 * into the legacy flat rows, deriving `depth` and `parentName` from tree
 * position. The contract carries no `updatedAt` — mapped '' (renders '-').
 */
export function mapOrganisationTree(nodes: GqlOrganisationUnitNode[]): OrganisationUnit[] {
  const rows: OrganisationUnit[] = [];
  const visit = (node: GqlOrganisationUnitNode, depth: number, parentName?: string): void => {
    const name = pickBilingualName(node.nameEn, node.nameZh);
    rows.push({
      id: node.id,
      code: node.code,
      name,
      parentName,
      depth,
      status: toRecordStatus(node.status),
      updatedAt: '',
    });
    for (const child of node.children ?? []) visit(child, depth + 1, name);
  };
  for (const root of nodes) visit(root, 0);
  return rows;
}

/**
 * `moduleCount` derivation: distinct first segments of the dotted permission
 * keys (`vendors.view` → `vendors`) — the same module vocabulary as
 * `routePermissions`. The contract carries no `updatedAt` — mapped ''.
 */
export function mapGovernanceRole(node: GqlGovernanceRoleNode): GovernanceRole {
  const modules = new Set(node.permissions.map((permission) => permission.split('.')[0]));
  return {
    id: node.id,
    name: node.name,
    description: node.description ?? '',
    memberCount: node.memberCount,
    moduleCount: modules.size,
    status: toRecordStatus(node.status),
    updatedAt: '',
  };
}

export function mapGovernanceUser(node: GqlGovernanceUserNode): GovernanceUser {
  const roleNames = [...new Set(node.assignments.map((assignment) => assignment.role.name))];
  const firstOrg = node.assignments.find((assignment) => assignment.organisationUnit != null);
  return {
    id: node.id,
    name: node.fullName,
    email: node.email,
    organisationName: firstOrg?.organisationUnit
      ? pickBilingualName(firstOrg.organisationUnit.nameEn, firstOrg.organisationUnit.nameZh)
      : '',
    roleNames,
    status: toUserStatus(node.status),
    lastLoginAt: node.lastActiveAt ?? undefined,
  };
}

/** The contract carries no `ipAddress` — mapped '' (renders '-'). */
export function mapGovernanceAuditLog(node: GqlGovernanceAuditNode): GovernanceAuditLog {
  return {
    id: node.id,
    operationType: toAuditOperation(node.operation),
    entityType: node.entityType,
    entityId: node.entityId,
    summary: node.summary,
    actorName: node.actor?.fullName ?? 'system',
    ipAddress: '',
    createdAt: node.at,
  };
}
